/**
 * STAR MERCENARIES 64: BOARDROOM NEBULA
 * Vibe Jam 2026 Entry
 * 
 * Main entry point - initializes and starts the game
 */

import { Game } from './game/Game.js';
import { Portal } from './game/Portal.js';
import { CHARACTER_PROFILES, getDefaultCharacterId } from './assets/CharacterProfiles.js';
import {
  getOpeningCinematicSteps,
  getSpeakerName,
  getSpeakerCinematicModelUrl,
  getStageOneColdOpenSteps,
  getStageOneBriefingSteps,
} from './narrative/stageOneScript.js';
import './loading.js'; // Initialize loading screen

// Global game instance
window.game = null;

// Loading progress
const loadingProgress = document.getElementById('loading-progress');
const loadingText = document.getElementById('loading-text');
const loadingScreen = document.getElementById('loading-screen');
const gameContainer = document.getElementById('game-container');

// Show the game canvas immediately; only show the boot overlay if init is "slow".
if (gameContainer) gameContainer.classList.add('visible');
let slowBootTimer = null;
if (loadingScreen) {
  slowBootTimer = setTimeout(() => {
    loadingScreen.classList.remove('hidden');
  }, 300);
}

function updateProgress(percent, text) {
  if (loadingProgress) loadingProgress.style.width = `${percent}%`;
  if (loadingText) loadingText.textContent = text;
}

function normalizeCharacterId(characterId) {
  return String(characterId || getDefaultCharacterId())
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

function getMergedUrlParams() {
  const params = new URLSearchParams(window.location.search || "");
  const hash = String(window.location.hash || "");
  const q = hash.indexOf("?");
  if (q >= 0) {
    const hashParams = new URLSearchParams(hash.slice(q + 1));
    for (const [k, v] of hashParams.entries()) {
      params.set(k, v);
    }
  }
  return params;
}

function isTruthyParam(value) {
  const v = String(value || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

// Initialize game
async function init() {
  try {
    updateProgress(10, 'Initializing engine...');
    
    // Parse URL parameters for portal integration
    const urlParams = getMergedUrlParams();
    const portalData = Portal.parseIncoming(urlParams);
    const autoplay = isTruthyParam(urlParams.get('autoplay')) || isTruthyParam(urlParams.get('demo'));
    
    updateProgress(20, 'Creating game world...');
    
    // Create game instance
    window.game = new Game({
      canvas: document.getElementById('game-canvas'),
      uiLayer: document.getElementById('ui-layer'),
      portalData,
      onProgress: updateProgress,
    });
    
    updateProgress(60, 'Loading assets...');
    
    // Initialize game (loads assets)
    await window.game.init();
    
    updateProgress(80, 'Preparing for launch...');
    
    // Initialize portal system
    Portal.init({
      scene: window.game.scene,
      getPlayer: () => window.game.player?.object3D,
      uiManager: window.game.uiManager,
    });
    
    updateProgress(90, 'Final checks...');
    
    // Hide loading screen + run pre-launch selection before starting.
    setTimeout(() => {
      (async () => {
        try {
          if (slowBootTimer) clearTimeout(slowBootTimer);
          slowBootTimer = null;
          if (loadingScreen) loadingScreen.classList.add('hidden');
          
	          updateProgress(100, 'Ready!');

          // First scene after boot: hangar banter, using full-profile hero GLBs where available.
          if (!autoplay) {
            try {
              if (!window.game.state._didShowStageOneColdOpen) {
                window.game.state._didShowStageOneColdOpen = true;
                const steps = getStageOneColdOpenSteps().map((s) => ({
                  ...s,
                  speakerName: s?.speakerName || (s?.speakerId ? getSpeakerName(s.speakerId) : ""),
                  modelUrl: s?.modelUrl || (s?.speakerId ? getSpeakerCinematicModelUrl(s.speakerId) : null),
                }));
                if (window.game.uiManager.showHangarIntroCinematic) {
                  await window.game.uiManager.showHangarIntroCinematic({
                    title: "STAR MERCENARIES 64",
                    steps,
                    skippable: true,
                    autoAdvance: true,
                  });
                } else {
                  await window.game.uiManager.showCinematicSequence({
                    title: "OPENING",
                    steps,
                    skippable: true,
                    enable3dModelPreview: true,
                  });
                }
              }
            } catch {
              // Best-effort; never block launch.
            }
          }

          if (autoplay && window.game?.enableAutoplay) {
            window.game.enableAutoplay({ mode: "figure8" });
            window.game.uiManager?.showMessage?.("AUTOPLAY ENABLED", "info", 1600);
            console.log("🧪 Autoplay enabled via URL params");
          }

          // Opening cinematic (skippable).
          if (!autoplay) {
            try {
              const steps = getOpeningCinematicSteps().map((s) => ({
                ...s,
                speakerName: s?.speakerName || (s?.speakerId ? getSpeakerName(s.speakerId) : ""),
              }));
              await window.game.uiManager.showCinematicSequence({
                title: "OPENING CINEMATIC",
                steps,
                skippable: true,
              });
            } catch {
              // Best-effort; never block launch.
            }
          }

	          // Character selection: if URL specifies a character, respect it; otherwise let the user choose.
	          let chosenId = normalizeCharacterId(window.game?.portalData?.character);
          const urlCharacter = urlParams.get('character');
          if (urlCharacter) chosenId = normalizeCharacterId(urlCharacter);

          if (!autoplay && !urlCharacter && !window.game?.portalData?.character) {
            let saved = null;
            try {
              saved = window.localStorage?.getItem('sm64_character');
            } catch {
              saved = null;
            }
            const selectedId = normalizeCharacterId(saved || chosenId);
            chosenId = await window.game.uiManager.showCharacterSelect({
              profiles: CHARACTER_PROFILES,
              selectedId,
            });
          }

          chosenId = normalizeCharacterId(chosenId);

          try {
            window.localStorage?.setItem('sm64_character', chosenId);
          } catch {
            // ignore
          }

	          // Ensure the chosen character is instantiated before launch.
	          if (window.game?.setPlayerCharacter) {
	            await window.game.setPlayerCharacter(chosenId);
	          }

	          // Stage One mission briefing + countdown (skippable).
	          if (!autoplay) {
	            try {
	              const steps = getStageOneBriefingSteps(chosenId).map((s) => ({
	                ...s,
	                speakerName: s?.speakerName || (s?.speakerId ? getSpeakerName(s.speakerId) : ""),
	              }));
	              await window.game.uiManager.showCinematicSequence({
	                title: "MISSION BRIEFING",
	                steps,
	                skippable: true,
	              });
	            } catch {
	              // Best-effort; never block launch.
	            }
	          }

	          // Start game systems
	          await window.game.start();
          window.game.setupFeedbackHooks();

          // Start portal animation loop
          Portal.startAnimationLoop();
          
          console.log('🚀 Star Mercenaries 64 launched!');
          console.log('📊 Vibe Jam 2026 Entry');
          console.log('🎮 Not heroes. Not villains. Contractors.');
        } catch (error) {
          console.error('Failed during pre-launch sequence:', error);
          if (loadingScreen) loadingScreen.classList.remove('hidden');
          if (loadingText) {
            loadingText.textContent = 'Error: ' + error.message;
            loadingText.style.color = '#ff4444';
          }
        }
      })();
    }, 500);
    
  } catch (error) {
    console.error('Failed to initialize game:', error);
    if (slowBootTimer) clearTimeout(slowBootTimer);
    slowBootTimer = null;
    if (loadingScreen) loadingScreen.classList.remove('hidden');
    if (loadingText) {
      loadingText.textContent = 'Error: ' + error.message;
      loadingText.style.color = '#ff4444';
    }
  }
}

// Handle page visibility (pause on tab switch)
document.addEventListener('visibilitychange', () => {
  if (window.game) {
    if (document.hidden) {
      window.game.pause();
    } else {
      window.game.resume();
    }
  }
});

// Handle window resize
window.addEventListener('resize', () => {
  if (window.game) {
    window.game.onResize();
  }
});

// Start initialization
init();

// Export for hot module replacement
export { init };
